/**
 * Iter134 — Photo Gallery Tab
 *
 * Two things:
 *   1. Chronological grid of every image evidence record on the case.
 *      Thumbnails are loaded with the user's auth token (/api/evidence/{id}/view)
 *      because /api/files/* requires a bearer header that <img> cannot send.
 *   2. In-tab upload queue — "Add Photos" selects one or many images,
 *      queues them locally, and streams each one through the SAME
 *      authoritative evidence pipeline (SHA-256, Object Storage, GridFS
 *      mirror, audit, chain-of-custody).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BACKEND_URL } from '../../config/canonical';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import {
  Camera, X, ChevronLeft, ChevronRight, Download, Image as ImageIcon,
  Plus, Upload, Loader2, CheckCircle2, AlertCircle, RotateCw, Trash2,
} from 'lucide-react';

const H = () => ({ Authorization: `Bearer ${localStorage.getItem('nexus_token')}` });

const isImageEv = (ev) => {
  if (ev.is_image === true) return true;
  if ((ev.media_type || '').toUpperCase() === 'IMAGE') return true;
  const ct = (ev.content_type || '').toLowerCase();
  const name = (ev.file_name || ev.name || '').toLowerCase();
  return ct.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|heic|heif|jfif|tiff?)$/i.test(name);
};

/* ---------- Authenticated thumbnail/img loader ---------- */
function AuthImg({ evidenceId, alt, className, onError }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/evidence/${evidenceId}/view`, { headers: H() });
        if (!r.ok) throw new Error(`view ${r.status}`);
        const blob = await r.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch (e) {
        if (!cancelled) { setFailed(true); onError && onError(e); }
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [evidenceId]); // eslint-disable-line

  if (failed) {
    return (
      <div className={`flex flex-col items-center justify-center text-center text-slate-500 bg-slate-900 ${className || ''}`}>
        <AlertCircle className="w-6 h-6 mb-1 text-amber-400" />
        <div className="text-[10px] font-mono px-1">File missing — admin can restore</div>
      </div>
    );
  }
  if (!src) return <div className={`bg-slate-800 animate-pulse ${className || ''}`} />;
  return <img src={src} alt={alt} loading="lazy" className={className} />;
}

/* ---------- Hashing (client preview only; server is authoritative) ---------- */
async function sha256Hex(file) {
  try {
    const buf = await file.arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch { return null; }
}

/* ---------- Main tab ---------- */
export default function PhotoGalleryTab({ caseId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(null);
  const [queue, setQueue] = useState([]); // {uid,file,status,progress,evidenceId?,error?,sha}
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases/${caseId}/evidence`, { headers: H() });
      if (r.ok) {
        const d = await r.json();
        const rows = (Array.isArray(d) ? d : (d.evidence || d.items || []))
          .filter(ev => !ev.is_deleted)
          .filter(isImageEv)
          .sort((a, b) => new Date(b.uploaded_at || b.created_at || 0) - new Date(a.uploaded_at || a.created_at || 0));
        setPhotos(rows);
      }
    } finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const out = {};
    for (const p of photos) {
      const d = new Date(p.uploaded_at || p.created_at || 0);
      const key = isNaN(d.valueOf()) ? 'Undated' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
      if (!out[key]) out[key] = [];
      out[key].push(p);
    }
    return out;
  }, [photos]);

  /* ---------- Queue management ---------- */
  const appendFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;
    const toAdd = [];
    for (const f of files) {
      const sha = await sha256Hex(f);
      const dupLocal = queue.some(q => q.sha && q.sha === sha);
      const dupRemote = photos.some(p => p.file_sha256 && p.file_sha256 === sha);
      toAdd.push({
        uid: `Q-${Math.random().toString(36).slice(2, 10)}`,
        file: f,
        status: dupLocal || dupRemote ? 'DUPLICATE' : 'READY',
        progress: 0,
        error: dupLocal ? 'Already in this queue' : dupRemote ? 'Already in this case' : null,
        sha,
        previewUrl: URL.createObjectURL(f),
      });
    }
    setQueue(prev => [...prev, ...toAdd]);
  };

  const onPickFiles = (e) => { appendFiles(e.target.files); e.target.value = ''; };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    appendFiles(e.dataTransfer?.files);
  };

  const removeFromQueue = (uid) => {
    setQueue(prev => {
      const target = prev.find(q => q.uid === uid);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(q => q.uid !== uid);
    });
  };

  const uploadOne = async (item) => {
    setQueue(prev => prev.map(q => q.uid === item.uid ? { ...q, status: 'UPLOADING', progress: 10, error: null } : q));
    try {
      const fd = new FormData();
      fd.append('file', item.file);
      fd.append('description', `Photo Gallery upload · ${new Date().toLocaleString()}`);
      fd.append('category', 'PHOTO_GALLERY');
      fd.append('evidence_type', 'PHOTO');
      // XHR gives us real progress events.
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/cases/${caseId}/evidence`);
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('nexus_token')}`);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            const pct = Math.round((ev.loaded / ev.total) * 70) + 10;
            setQueue(prev => prev.map(q => q.uid === item.uid ? { ...q, progress: pct } : q));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText || '{}')); } catch { resolve({}); }
          } else {
            let msg = xhr.statusText || `HTTP ${xhr.status}`;
            try { const d = JSON.parse(xhr.responseText || '{}'); if (d.detail) msg = d.detail; } catch {}
            reject(new Error(msg));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(fd);
      });
      setQueue(prev => prev.map(q => q.uid === item.uid
        ? { ...q, status: 'COMPLETE', progress: 100, evidenceId: result.id }
        : q));
      await load();
    } catch (e) {
      setQueue(prev => prev.map(q => q.uid === item.uid
        ? { ...q, status: 'FAILED', progress: 0, error: e.message || 'Upload failed' }
        : q));
    }
  };

  const uploadAll = async () => {
    const pending = queue.filter(q => q.status === 'READY' || q.status === 'FAILED');
    for (const item of pending) {
      // sequential to be gentle on the backend; swap for Promise.all for parallel.
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(item);
    }
  };

  const retry = (uid) => {
    const item = queue.find(q => q.uid === uid);
    if (item) uploadOne(item);
  };

  const clearCompleted = () => {
    setQueue(prev => prev.filter(q => q.status !== 'COMPLETE').map(q => {
      // keep previewUrl for retrying items
      return q;
    }));
  };

  /* ---------- Lightbox ---------- */
  const openAt = (p) => setActiveIdx(photos.findIndex(x => x.id === p.id));
  const close = () => setActiveIdx(null);
  const prev = () => setActiveIdx(i => (i == null ? i : (i - 1 + photos.length) % photos.length));
  const next = () => setActiveIdx(i => (i == null ? i : (i + 1) % photos.length));

  useEffect(() => {
    if (activeIdx == null) return;
    const h = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [activeIdx, photos.length]);

  const activePhoto = activeIdx == null ? null : photos[activeIdx];

  const statusColor = (s) => ({
    READY: 'text-slate-300 bg-slate-700',
    UPLOADING: 'text-blue-300 bg-blue-500/20',
    COMPLETE: 'text-emerald-300 bg-emerald-500/20',
    FAILED: 'text-red-300 bg-red-500/20',
    DUPLICATE: 'text-amber-300 bg-amber-500/20',
  }[s] || 'text-slate-300 bg-slate-700');

  return (
    <div className="space-y-4" data-testid="case-photo-gallery-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
            <Camera className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-amber-300">CASE · PHOTOS</div>
            <h2 className="text-xl font-bold text-white">Photo Gallery</h2>
            <p className="text-xs text-slate-400">
              {photos.length} photo{photos.length === 1 ? '' : 's'} · newest first
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="file" accept="image/*" multiple capture="environment"
            className="hidden" onChange={onPickFiles} data-testid="photo-picker-input" />
          <Button onClick={() => inputRef.current?.click()}
            className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
            data-testid="photo-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Photos
          </Button>
        </div>
      </div>

      {/* Drop zone / Queue */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-lg border-2 border-dashed p-3 ${dragOver ? 'border-amber-400 bg-amber-500/5' : 'border-slate-700 bg-slate-900/40'}`}
        data-testid="photo-queue-dropzone"
      >
        {queue.length === 0 ? (
          <div className="text-center text-xs text-slate-500 py-4">
            Drag and drop images here, or click <span className="text-amber-300 font-semibold">Add Photos</span>.
            Images are hashed, stored in Object Storage + Mongo GridFS, and added as evidence to this case.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-slate-300">
                <span className="font-semibold">{queue.length}</span> in queue ·{' '}
                <span className="text-emerald-300">{queue.filter(q => q.status === 'COMPLETE').length} complete</span> ·{' '}
                <span className="text-blue-300">{queue.filter(q => q.status === 'UPLOADING').length} uploading</span> ·{' '}
                <span className="text-red-300">{queue.filter(q => q.status === 'FAILED').length} failed</span>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={uploadAll}
                  className="bg-blue-600 hover:bg-blue-500 text-white h-8"
                  disabled={queue.every(q => q.status === 'COMPLETE' || q.status === 'DUPLICATE' || q.status === 'UPLOADING')}
                  data-testid="photo-upload-all-btn">
                  <Upload className="w-3 h-3 mr-1" /> Upload All
                </Button>
                <Button size="sm" variant="outline" onClick={clearCompleted}
                  className="h-8 text-xs border-slate-600 text-slate-200"
                  data-testid="photo-clear-complete-btn">
                  <Trash2 className="w-3 h-3 mr-1" /> Clear Complete
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {queue.map(q => (
                <div key={q.uid}
                  className="relative rounded border border-slate-700 overflow-hidden bg-slate-800"
                  data-testid={`photo-queue-item-${q.uid}`}>
                  <div className="aspect-square relative">
                    <img src={q.previewUrl} alt={q.file.name} className="w-full h-full object-cover opacity-80" />
                    {q.status === 'UPLOADING' && (
                      <div className="absolute inset-x-0 bottom-0 bg-slate-900/80">
                        <div className="h-1 bg-blue-500" style={{ width: `${q.progress}%` }} />
                      </div>
                    )}
                    {q.status === 'COMPLETE' && (
                      <div className="absolute inset-0 bg-emerald-500/20 flex items-center justify-center">
                        <CheckCircle2 className="w-8 h-8 text-emerald-300" />
                      </div>
                    )}
                    {q.status === 'FAILED' && (
                      <div className="absolute inset-0 bg-red-500/30 flex items-center justify-center">
                        <AlertCircle className="w-8 h-8 text-red-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-1.5 space-y-1">
                    <div className="text-[10px] text-white truncate" title={q.file.name}>{q.file.name}</div>
                    <div className="flex items-center justify-between gap-1">
                      <span className={`text-[9px] font-mono px-1 py-0.5 rounded ${statusColor(q.status)}`}>
                        {q.status}
                      </span>
                      <span className="text-[9px] text-slate-500 font-mono">
                        {(q.file.size / 1024).toFixed(0)} KB
                      </span>
                    </div>
                    {q.error && <div className="text-[9px] text-red-300 truncate" title={q.error}>{q.error}</div>}
                    <div className="flex items-center gap-1">
                      {q.status === 'FAILED' && (
                        <button onClick={() => retry(q.uid)}
                          className="text-[9px] text-blue-300 hover:text-blue-200 inline-flex items-center gap-0.5"
                          data-testid={`photo-queue-retry-${q.uid}`}>
                          <RotateCw className="w-2.5 h-2.5" /> Retry
                        </button>
                      )}
                      {q.status !== 'UPLOADING' && (
                        <button onClick={() => removeFromQueue(q.uid)}
                          className="text-[9px] text-slate-400 hover:text-red-300 ml-auto inline-flex items-center gap-0.5"
                          data-testid={`photo-queue-remove-${q.uid}`}>
                          <X className="w-2.5 h-2.5" /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Existing gallery */}
      {loading ? (
        <div className="text-sm text-slate-500 py-6 text-center">Loading photos…</div>
      ) : photos.length === 0 ? (
        <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-10 text-center" data-testid="case-photo-gallery-empty">
          <ImageIcon className="w-10 h-10 mx-auto text-slate-500 mb-2" />
          <p className="text-slate-400">No photos attached to this case yet.</p>
          <p className="text-slate-500 text-xs mt-1">Use Add Photos above, Log Field Event, or Evidence Upload.</p>
        </div>
      ) : (
        Object.entries(grouped).map(([day, rows]) => (
          <div key={day} className="space-y-2">
            <h3 className="text-xs font-mono uppercase tracking-wider text-slate-400">{day} · {rows.length}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {rows.map(p => (
                <button key={p.id}
                  onClick={() => openAt(p)}
                  data-testid={`case-photo-${p.id}`}
                  className="group relative aspect-square rounded-lg overflow-hidden bg-slate-800 border border-slate-700 hover:border-amber-500/60 transition">
                  <AuthImg evidenceId={p.id} alt={p.file_name || p.name} className="w-full h-full object-cover group-hover:scale-105 transition" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2">
                    <div className="text-[10px] font-mono text-white truncate">
                      {new Date(p.uploaded_at || p.created_at || 0).toLocaleTimeString()}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Lightbox */}
      {activePhoto && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" onClick={close} data-testid="case-photo-lightbox">
          <div className="flex items-center justify-between p-3 border-b border-slate-800" onClick={e => e.stopPropagation()}>
            <div className="text-sm text-white truncate pr-4">
              {activePhoto.file_name || activePhoto.name || activePhoto.id}
              <span className="ml-3 text-slate-400 font-mono text-xs">
                {new Date(activePhoto.uploaded_at || activePhoto.created_at || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={async () => {
                const r = await fetch(`${BACKEND_URL}/api/evidence/${activePhoto.id}/download`, { headers: H() });
                if (!r.ok) { toast({ title: 'Download failed', variant: 'destructive' }); return; }
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = activePhoto.file_name || `photo-${activePhoto.id}`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
              }}
                className="text-slate-200 hover:text-amber-300 px-2 py-1 flex items-center gap-1 text-xs" data-testid="case-photo-download">
                <Download className="w-4 h-4" /> Download
              </button>
              <button onClick={close} className="text-slate-200 hover:text-white" data-testid="case-photo-close">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center relative" onClick={e => e.stopPropagation()}>
            <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2" data-testid="case-photo-prev">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <AuthImg evidenceId={activePhoto.id} alt="" className="max-w-full max-h-[85vh] object-contain" />
            <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2" data-testid="case-photo-next">
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
